import React, { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  RefreshCw,
  Gift,
  Trophy,
  Coins,
  ArrowDownRight,
  Percent,
  Ticket,
} from "lucide-react";
import { SpinAWheelApi, SpinWheelConfig, ScratchCardApi, ScratchCardConfig } from "@/api/giftcard.api";
import { HandleError } from "@/api/axiosClient";
import { useToast } from "@/hooks/use-toast";
import { format } from "date-fns";

const stats = [
  {
    title: "Total Spin",
    value: "5,240",
    icon: RefreshCw,
    color: "text-blue-500",
    bgColor: "bg-blue-100",
  },
  {
    title: "Total Scratch",
    value: "3,120",
    icon: Gift,
    color: "text-purple-500",
    bgColor: "bg-purple-100",
  },
  {
    title: "Total Winners",
    value: "1,480",
    icon: Trophy,
    color: "text-yellow-500",
    bgColor: "bg-yellow-100",
  },
  {
    title: "Points Issued",
    value: "250,000",
    icon: Coins,
    color: "text-emerald-500",
    bgColor: "bg-emerald-100",
  },
  {
    title: "Points Redeemed",
    value: "185,000",
    icon: ArrowDownRight,
    color: "text-red-500",
    bgColor: "bg-red-100",
  },
  {
    title: "Conversion Rate",
    value: "62%",
    icon: Percent,
    color: "text-indigo-500",
    bgColor: "bg-indigo-100",
  },
  {
    title: "Coupons Issued",
    value: "12,500",
    icon: Ticket,
    color: "text-teal-500",
    bgColor: "bg-teal-100",
  },
  {
    title: "Coupons Redeemed",
    value: "8,900",
    icon: Ticket,
    color: "text-pink-500",
    bgColor: "bg-pink-100",
  },
  {
    title: "Coupons Conversion Rate",
    value: "71%",
    icon: Percent,
    color: "text-sky-500",
    bgColor: "bg-sky-100",
  },
];

export function GamificationAnalytics() {
  const { toast } = useToast();
  const [spinWheels, setSpinWheels] = useState<SpinWheelConfig[]>([]);
  const [scratchCards, setScratchCards] = useState<ScratchCardConfig[]>([]);
  const [isLoadingSpin, setIsLoadingSpin] = useState(false);
  const [isLoadingScratch, setIsLoadingScratch] = useState(false);

  useEffect(() => {
    fetchSpinWheels();
    fetchScratchCards();
  }, []);

  const fetchSpinWheels = async () => {
    setIsLoadingSpin(true);
    try {
      const response = await SpinAWheelApi.getAllSpinAwheelConfig({});
      if (response.error_msg && response.error_msg.trim() !== "") {
        toast({
          title: "Error",
          description: response.error_msg,
          variant: "destructive",
        });
      } else {
        setSpinWheels(response.getSpinAwheelConfig || []);
      }
    } catch (error: any) {
      console.error("Error fetching spin wheel analytics:", error);
      const errorMessage = HandleError(error);
      toast({
        title: "Error",
        description: errorMessage || "Failed to fetch spin wheel data",
        variant: "destructive",
      });
    } finally {
      setIsLoadingSpin(false);
    }
  };

  const fetchScratchCards = async () => {
    setIsLoadingScratch(true);
    try {
      const response = await ScratchCardApi.getAllScratchCardConfig({});

      if (response.error_msg && response.error_msg.trim() !== "") {
        toast({
          title: "Error",
          description: response.error_msg,
          variant: "destructive",
        });
      } else {
        setScratchCards(response.scratchCard || []);
      }
    } catch (error: any) {
      console.error("Error fetching scratch card analytics:", error);
      const errorMessage = HandleError(error);
      toast({
        title: "Error",
        description: errorMessage || "Failed to fetch scratch card data",
        variant: "destructive",
      });
    } finally {
      setIsLoadingScratch(false);
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return "-";
    try {
      const date = new Date(dateString);
      return format(date, "MMM dd, yyyy");
    } catch {
      return dateString;
    }
  };

  const getScratchRewardTypeLabel = (rewardType: number) =>
    rewardType === 0 ? "Points" : "Cash";

  const getScratchStatusLabel = (isActive: boolean) =>
    isActive ? "Active" : "Inactive";

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-medium text-gray-800">Analytics</h2>
        <p className="text-sm text-muted-foreground">
          Overview of your gamification performance.
        </p>
      </div>

      {/* Top Stat Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {stats.map((stat) => (
          <Card key={stat.title}>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                {stat.title}
              </CardTitle>
              <div className={`${stat.bgColor} p-2 rounded-md`}>
                <stat.icon className={`h-4 w-4 ${stat.color}`} />
              </div>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stat.value}</div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Spin Wheel Table */}
      <Card>
        <CardHeader>
          <CardTitle>Spin Wheel</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            {isLoadingSpin ? (
              <div className="py-6 text-sm text-muted-foreground">
                Loading spin wheel data...
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Spin Wheel Name</TableHead>
                    <TableHead>Start Date</TableHead>
                    <TableHead>End Date</TableHead>
                    <TableHead>No of Slots</TableHead>
                    <TableHead>Spin / Member</TableHead>
                    <TableHead>Bonus Validity (Days)</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {spinWheels.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={6}
                        className="py-6 text-center text-sm text-muted-foreground"
                      >
                        No spin wheel configurations found.
                      </TableCell>
                    </TableRow>
                  ) : (
                    spinWheels.slice(-5).map((row) => (
                      <TableRow key={row.id}>
                        <TableCell>{row.spinName}</TableCell>
                        <TableCell>{formatDate(row.startDate)}</TableCell>
                        <TableCell>{formatDate(row.endDate)}</TableCell>
                        <TableCell>{row.noOfSlots}</TableCell>
                        <TableCell>{row.spinPerMember}</TableCell>
                        <TableCell>{row.bonusValidityInDay}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Scratch Card Table */}
      <Card>
        <CardHeader>
          <CardTitle>Scratch Card</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            {isLoadingScratch ? (
              <div className="py-6 text-sm text-muted-foreground">
                Loading scratch card data...
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Scratch Card Name</TableHead>
                    <TableHead>Reward Type</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Start Date</TableHead>
                    <TableHead>End Date</TableHead>
                    <TableHead>Small Reward</TableHead>
                    <TableHead>Medium Reward</TableHead>
                    <TableHead>Big Reward</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {scratchCards.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={8}
                        className="py-6 text-center text-sm text-muted-foreground"
                      >
                        No scratch card configurations found.
                      </TableCell>
                    </TableRow>
                  ) : (
                    scratchCards.slice(-5).map((row) => (
                      <TableRow key={row.id}>
                        <TableCell>{row.name}</TableCell>
                        <TableCell>
                          {getScratchRewardTypeLabel(row.rewardType)}
                        </TableCell>
                        <TableCell>
                          {getScratchStatusLabel(row.isActive)}
                        </TableCell>
                        <TableCell>{formatDate(row.startDate)}</TableCell>
                        <TableCell>{formatDate(row.endDate)}</TableCell>
                        <TableCell>{row.smallReward}</TableCell>
                        <TableCell>{row.mediumReward}</TableCell>
                        <TableCell>{row.bigReward}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
